import { useEffect, useMemo } from "react";
import { ItemModel } from "../../../../../../infrastructure/model/ProductItemModel";
import { SaleInfoResponse } from "../../../../../../infrastructure/model/SaleInfoModel";
import {
  FlashDealItem,
  getCupSaleBadgeText,
  getFlashDealItems,
  getFlashDealSummary,
} from "../../../../../../applications/useCase/BusinessUseCase/FlashDealUseCase";
import FlashCountdown from "./FlashCountdown";
import ViewAllButton from "./ViewAllButton";
import "../../styles/FlashDealSection.scss";
import { HOME_PAGE_MAX_SOW_SECTION } from "../../../../../../applications/constants/AppConst";

interface FlashDealSectionProps {
  items: ItemModel[];
  sales: SaleInfoResponse[]; // Thêm prop này
  onAdd: (item: ItemModel) => void;
  sectionKey: string;
  onShowAll?: (val: string) => void;
  isShowAll?: boolean;
}

function FlashDealSection(props: FlashDealSectionProps) {
  const { items, sales, onAdd, sectionKey, onShowAll, isShowAll = false } = props;

  // Tính toán flash deal items
  const flashDealData = useMemo(() => {
    const flashDealItems = getFlashDealItems(items, sales);
    const summary = getFlashDealSummary(flashDealItems);
    return { flashDealItems, summary };
  }, [items, sales]);

  const { flashDealItems, summary } = flashDealData;

  // Nếu không có flash deal nào, không hiển thị section
  if (flashDealItems.length === 0) {
    return null;
  }

  const showItems = isShowAll ? flashDealItems : flashDealItems.slice(0, HOME_PAGE_MAX_SOW_SECTION);

  const { totalStock, sold, remaining, percent, nearestEndTime, cupDealsCount } = summary;

  return (
    <section className={`home-page-section flash-deal-pro`}>
      {/* HEADER */}
      <div className="section-header">
        <h2>
          <span className="section-icon">⚡⚡</span>
          FLASH DEAL
          {/* {cupDealsCount > 0 && (
            <span className="cup-deal-badge">🎁 {cupDealsCount} deal mua 1 tặng 1</span>
          )} */}
        </h2>

        <div className="flash-header-right">
          {nearestEndTime && <FlashCountdown endTime={new Date(nearestEndTime).toISOString()} />}
          <span className="flash-badge">SALE SỐC</span>
        </div>
      </div>

      {/* STOCK BAR - Chỉ hiển thị nếu có thông tin stock */}
      {totalStock > 0 && (
        <div className="flash-stock">
          <div className="stock-bar">
            <div className="stock-bar-fill" style={{ width: `${percent}%` }} />
          </div>
          <span>Còn {remaining} suất</span>
        </div>
      )}

      {/* CONTENT */}
      <div className="section-content">
        <div className="products-grid">
          {showItems.map((item) => (
            <FlashDealCard key={item.id} item={item} onAdd={onAdd} />
          ))}
        </div>
      </div>

      {flashDealItems.length > HOME_PAGE_MAX_SOW_SECTION && !isShowAll && (
        <ViewAllButton
          onClick={() => onShowAll?.(sectionKey)}
          highlight="flash"
          label="Xem tất cả Flash Deal"
        />
      )}
    </section>
  );
}

// ============= FLASH DEAL CARD COMPONENT =============
interface FlashDealCardProps {
  item: FlashDealItem;
  onAdd: (item: ItemModel) => void;
}

function FlashDealCard({ item, onAdd }: FlashDealCardProps) {
  const { saleInfo } = item;

  return (
    <div className="product-card flash-card">
      {/* Badge hiển thị loại deal */}
      {saleInfo.isCupSale ? (
        <span className="discount-badge cup-badge">
          {getCupSaleBadgeText(saleInfo.cupBuyQuantity || 0, saleInfo.cupFreeQuantity || 0)}
        </span>
      ) : (
        <span className="discount-badge">-{saleInfo.discountPercent}%</span>
      )}

      <div className="image-wrapper">
        <img src={item.image} alt={item.name} />
        <button className="add-btn" onClick={() => onAdd(item)}>
          +
        </button>
      </div>

      <div className="product-info">
        <h4>{item.name}</h4>

        {/* Hiển thị giá khác nhau cho CUP sale và normal sale */}
        {saleInfo.isCupSale ? (
          <div className="price-box cup-price-box">
            <div className="cup-deal-info">
              <span className="cup-deal-label">{saleInfo.cupDealText}</span>
              <span className="cup-deal-desc">
                &nbsp; Chỉ từ {saleInfo.originalPrice.toLocaleString()}đ/cốc
              </span>
            </div>
            <span className="price-origin">
              Giá gốc: {saleInfo.originalPrice.toLocaleString()}đ
            </span>
          </div>
        ) : (
          <div className="price-box">
            <span className="price-sale">{saleInfo.salePrice.toLocaleString()}đ</span>
            <span className="price-origin">{saleInfo.originalPrice.toLocaleString()}đ</span>
          </div>
        )}
        {/* Hiển thị remaining nếu có */}
        {saleInfo.remaining !== undefined && saleInfo.totalStock && (
          <div className="item-stock-info">
            <div className="mini-progress">
              <div
                className="mini-progress-fill"
                style={{
                  width: `${Math.round(
                    ((saleInfo.totalStock - saleInfo.remaining) / saleInfo.totalStock) * 100
                  )}%`,
                }}
              />
            </div>
            <span className="stock-text">Còn {saleInfo.remaining}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default FlashDealSection;

// ============= CSS SUGGESTIONS =============
/*
Thêm vào CSS của bạn:

.item-stock-info {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mini-progress {
  width: 100%;
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
}

.mini-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6b6b, #ff8787);
  transition: width 0.3s ease;
}

.stock-text {
  font-size: 11px;
  color: #ff6b6b;
  font-weight: 600;
}
*/
